import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, MailCheck, ArrowLeft } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { Logo } from "@/components/Brand";
import { useCms } from "@/lib/cms";

export default function ForgotPassword() {
  const { site } = useCms();
  const brandName = site?.company_name || "";
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(79,70,229,0.35),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.22),transparent_55%)]" />
        <div className="absolute inset-0 dot-bg opacity-30" />
        <Logo className="relative text-white [&_span]:!text-white" />
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="relative">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400 mb-4">Account recovery</div>
          <h2 className="font-display text-4xl font-semibold leading-tight">Locked out? We&apos;ll get you back in.</h2>
          <p className="mt-4 text-sm text-gray-400 max-w-sm">Enter the email tied to your {brandName} account and we&apos;ll send a secure reset link.</p>
        </motion.div>
        <div className="relative text-xs text-gray-400">© {brandName} Institute</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-6" data-testid="forgot-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
          {sent ? (
            <div className="text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 grid place-items-center mb-4">
                <MailCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="font-display text-2xl font-semibold">Check your inbox</h1>
              <p className="text-sm text-gray-500 mt-2" data-testid="forgot-sent-msg">
                If an account exists for <span className="font-medium text-gray-800">{email}</span>, we&apos;ve sent a reset link. It expires in one hour.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="mt-6 text-xs font-medium text-[#4F46E5] hover:underline"
                data-testid="forgot-try-another"
              >
                Try a different email
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h1 className="font-display text-3xl font-semibold">Forgot password</h1>
              <p className="text-sm text-gray-500 mt-1">We&apos;ll email you a link to reset it.</p>
              <div className="mt-6 space-y-3">
                <input
                  required type="email" placeholder="Email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                  className="w-full px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm transition-all"
                />
                <button
                  type="submit" disabled={loading} data-testid="forgot-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0F172A] disabled:bg-gray-400 hover:bg-[#1e293b] text-white text-sm font-medium px-4 py-3 rounded-lg transition-colors"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
